import { useCallback, useEffect, useState } from 'react'

import { FileToUpload } from 'src/utils/fileUpload'

export const useUploadProgress = () => {
  const [isUploading, setIsUploading] = useState(false)
  const [progress, setProgress] = useState<{
    filesCount: number
    filesComplete: number
    loadedBytes: number
    totalBytes: number
    percentageComplete: number
  }>({
    filesComplete: 0,
    filesCount: 0,
    loadedBytes: 0,
    percentageComplete: 0,
    totalBytes: 0
  })

  useEffect(() => {
    if (!isUploading) {
      return
    }
    const confirmWindowClose = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }

    window.addEventListener('beforeunload', confirmWindowClose)

    return () => {
      console.log('REMOVING EVENT LISTENER')
      window.removeEventListener('beforeunload', confirmWindowClose)
    }
  }, [isUploading])

  const handleUploadProgress = useCallback((files: FileToUpload[]) => {
    setIsUploading(true)
    const totalLoaded = files.reduce<
      Record<string, { loaded: number; total: number }>
    >((acc, curr) => {
      return {
        ...acc,
        [curr.file.name]: {
          loaded: 0,
          total: 0
        }
      }
    }, {})

    const totalSizeToUpload = files.reduce(
      (acc, curr) => acc + curr.file.size,
      0
    )

    const setFileProgress = (key: string, loaded: number, total: number) => {
      totalLoaded[key] = { loaded, total }
      const progressValues = Object.values(totalLoaded)

      const loadedBytes = progressValues.reduce(
        (acc, curr) => acc + curr.loaded,
        0
      )

      const filesComplete = progressValues.reduce((acc, curr) => {
        if (curr.total !== 0 && curr.total === curr.loaded) {
          return acc + 1
        }

        return acc
      }, 0)

      setProgress(prev => ({
        ...prev,
        filesComplete,
        loadedBytes,
        percentageComplete: (loadedBytes / totalSizeToUpload) * 100
      }))
      if (filesComplete === files.length) {
        setIsUploading(false)
      }
    }

    setProgress({
      filesComplete: 0,
      filesCount: files.length,
      loadedBytes: 0,
      percentageComplete: 0,
      totalBytes: totalSizeToUpload
    })

    return {
      setFileProgress
    }
  }, [])

  return {
    handleUploadProgress,
    isUploading,
    progress
  }
}
